import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import "../styles/BarChart.scss";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Default color palette
const DEFAULT_COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#a4de6c",
  "#d0ed57",
];

const BarChart = ({ unit, yAxisTitle, chartTitle, data, xKey, barsConfig }) => {
  const safeData = Array.isArray(data) ? data : [];
  const safeBars = Array.isArray(barsConfig) ? barsConfig : [];
  const [hiddenBars, setHiddenBars] = useState({});

  // --- BYTE FORMATTING LOGIC ---
  // for charts that display data in bytes, will format all datas to kb or mb etc..
  const { chartData, finalYAxisTitle, displayUnit } = useMemo(() => {
    if (unit !== "bytes" || safeData.length === 0) {
      return { chartData: safeData, finalYAxisTitle: yAxisTitle, displayUnit: "" };
    }
    const units = ["b", "kb", "mb", "gb", "tb"];
    // 1. Find the max value across all bar keys to determine the best unit for the whole chart
    let maxVal = 0;
    safeData.forEach(item => {
      safeBars.forEach(bar => {
        const val = parseFloat(item[bar.key]) || 0;
        if (val > maxVal) maxVal = val;
      });
    });
    // 2. Determine index (0 for b, 1 for kb, etc)
    let index = maxVal > 0 ? Math.floor(Math.log(maxVal) / Math.log(1000)) : 0;
    index = Math.min(index, units.length - 1);
    const selectedUnit = units[index];
    const divisor = Math.pow(1000, index);
    // 3. Transform data so Recharts renders the smaller floats
    const transformedData = safeData.map(item => {
      const newItem = { ...item };
      safeBars.forEach(bar => {
        const originalValue = parseFloat(item[bar.key]) || 0;
        newItem[bar.key] = parseFloat((originalValue / divisor).toFixed(2));
      });
      return newItem;
    });

    return {
      chartData: transformedData,
      finalYAxisTitle: `Size (${selectedUnit.toUpperCase()})`,
      displayUnit: selectedUnit
    };
  }, [safeData, safeBars, unit, yAxisTitle]);

  const formatWithSpaces = (value) =>
    String(value).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  const formatXAxis = (value) => {
    const date = new Date(value);
    if (!isNaN(date)) {
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
    return String(value);
  };

  if (!xKey) {
    console.warn("BarChart: 'xKey' prop is missing");
    return <div className="bc-no-data">No x-axis key provided</div>;
  }

  const tickValues = safeData.map((item) => item[xKey]);

  const handleLegendClick = (e) => {
    const { dataKey } = e;
    setHiddenBars((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  return (
    <div className="bc-main-container">
      {chartTitle && <span className="bc-title">{chartTitle}</span>}

      <ResponsiveContainer width="100%" height={300}>
        <RechartsBarChart
          data={chartData}
          barCategoryGap="20%"
          barGap={4}
          margin={{ top: 20, right: 30, left: 30, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />

          <XAxis
            dataKey={xKey}
            tickFormatter={formatXAxis}
            stroke="#fff"
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={60}
            interval={Math.ceil(safeData.length / 14) - 1}
          />

          <YAxis
            stroke="#fff"
            tick={{ fontSize: 12 }}
            tickFormatter={(val) => `${val} ${displayUnit}`}
            label={
              finalYAxisTitle
                ? {
                    value: finalYAxisTitle,
                    angle: -90,
                    position: "insideLeft",
                    fill: "#fff",
                    style: { textAnchor: "middle" },
                    offset: -10
                  }
                : undefined
            }
          />

          <Tooltip
            contentStyle={{
              backgroundColor: "rgba(35, 50, 70, 0.8)",
              border: "1px solid #8884d8",
              borderRadius: "4px",
              color: "#fff",
              backdropFilter: "blur(10px)",
            }}
            formatter={(value) => [`${value} ${displayUnit}`, ""]}
          />

          <Legend verticalAlign="top" align="center" onClick={handleLegendClick} />

          {safeBars.map((bar, idx) => (
            <Bar
              key={bar.key}
              dataKey={bar.key}
              name={bar.name || bar.key}
              stackId={bar.stackId} // Supports your stacking logic
              fill={bar.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
              hide={!!hiddenBars[bar.key]}
              barSize={22}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

BarChart.propTypes = {
  chartTitle: PropTypes.string,
  yAxisTitle: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object),
  xKey: PropTypes.string.isRequired,
  barsConfig: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string,
      color: PropTypes.string,
    })
  ),
};

BarChart.defaultProps = {
  data: [],
  barsConfig: [],
  yAxisTitle: "",
};

export default BarChart;
